import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const TermsOfServicePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border backdrop-blur-sm bg-background/95 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/')}
                className="hover:bg-accent/10 hover:text-accent"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity bg-transparent border-none cursor-pointer"
              >
                <img src="/logo.png" alt="Shoot Base" className="h-14 md:h-20 w-auto object-contain" />
                <h1 className="text-xl md:text-2xl font-bold text-accent" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Shoot Base
                </h1>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-b from-orange-ultralight to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge className="mb-4 bg-accent/10 text-accent border-accent/30">
              <FileText className="w-3 h-3 mr-1" />
              Terms of Service
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
              Terms of <span className="text-accent">Service</span>
            </h1>
            <p className="text-sm text-muted-foreground">
              Last Updated: December 25, 2024
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-slate">
            <div className="space-y-8 text-muted-foreground">
              
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">1. Acceptance of Terms</h2>
                <p className="leading-relaxed">
                  Welcome to Shoot Base. By accessing or using our platform, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our services.
                </p>
                <p className="leading-relaxed mt-3">
                  These Terms constitute a legally binding agreement between you and Shoot Base. We reserve the right to modify these Terms at any time, and your continued use of the platform constitutes acceptance of any changes.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">2. Eligibility</h2>
                <p className="leading-relaxed">To use Shoot Base, you must:</p>
                <ul className="list-disc pl-6 space-y-1 mt-2">
                  <li>Be at least 18 years of age</li>
                  <li>Have the legal capacity to enter into binding contracts</li>
                  <li>Not be prohibited from using our services under Indian law</li>
                  <li>Provide accurate and complete registration information</li>
                  <li>Maintain the security of your account credentials</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  By creating an account, you represent and warrant that you meet all eligibility requirements.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">3. Account Responsibilities</h2>
                
                <h3 className="text-xl font-semibold text-foreground mt-4 mb-2">3.1 Account Creation</h3>
                <p className="leading-relaxed">
                  You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to:
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-2">
                  <li>Provide truthful, accurate, and complete information</li>
                  <li>Update your information to keep it current</li>
                  <li>Notify us immediately of any unauthorized use of your account</li>
                  <li>Not share your password or account access with others</li>
                </ul>

                <h3 className="text-xl font-semibold text-foreground mt-4 mb-2">3.2 Account Termination</h3>
                <p className="leading-relaxed">
                  We reserve the right to suspend or terminate your account at any time for violations of these Terms, fraudulent activity, or at our sole discretion. You may also delete your account at any time through your account settings.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">4. Platform Usage Rules</h2>
                
                <h3 className="text-xl font-semibold text-foreground mt-4 mb-2">4.1 Permitted Use</h3>
                <p className="leading-relaxed">
                  Shoot Base is a platform for connecting film professionals with production houses and clients. You may use our services to:
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-2">
                  <li>Create a professional profile showcasing your skills and experience</li>
                  <li>Browse and search for film professionals by location and department</li>
                  <li>Post hiring requests for film projects</li>
                  <li>Communicate with other users through our platform</li>
                  <li>Review and rate professionals you've worked with</li>
                </ul>

                <h3 className="text-xl font-semibold text-foreground mt-4 mb-2">4.2 Prohibited Conduct</h3>
                <p className="leading-relaxed">You agree not to:</p>
                <ul className="list-disc pl-6 space-y-1 mt-2">
                  <li>Post false, misleading, or fraudulent information</li>
                  <li>Impersonate any person or entity</li>
                  <li>Harass, abuse, or harm other users</li>
                  <li>Upload malicious code, viruses, or harmful content</li>
                  <li>Scrape, crawl, or collect data from our platform without permission</li>
                  <li>Use our services for illegal activities</li>
                  <li>Circumvent or interfere with security features</li>
                  <li>Spam or send unsolicited communications</li>
                  <li>Violate intellectual property rights</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">5. Professional Verification</h2>
                <p className="leading-relaxed">
                  Shoot Base offers a verification program for film professionals. Verification is based on review of credentials, portfolio, and work history. However:
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-2">
                  <li>Verification does not guarantee skill level or work quality</li>
                  <li>We are not responsible for verifying all claims made by professionals</li>
                  <li>Clients should conduct their own due diligence before hiring</li>
                  <li>Verification status may be revoked if false information is discovered</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  <strong>Disclaimer:</strong> Shoot Base serves as a connection platform only. We do not employ or endorse any professionals listed on our platform.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">6. Content Ownership and License</h2>
                
                <h3 className="text-xl font-semibold text-foreground mt-4 mb-2">6.1 Your Content</h3>
                <p className="leading-relaxed">
                  You retain ownership of all content you submit to Shoot Base (profile information, portfolio images, project descriptions, etc.). By uploading content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and display your content for the purpose of operating and promoting our services.
                </p>

                <h3 className="text-xl font-semibold text-foreground mt-4 mb-2">6.2 Platform Content</h3>
                <p className="leading-relaxed">
                  All content, features, and functionality of the Shoot Base platform, including text, graphics, logos, and software, are owned by Shoot Base and protected by copyright, trademark, and other intellectual property laws.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">7. Payment Terms</h2>
                <p className="leading-relaxed">
                  Currently, Shoot Base does not facilitate payments between clients and professionals. All payment arrangements, negotiations, and transactions are conducted directly between users. We are not responsible for:
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-2">
                  <li>Payment disputes between users</li>
                  <li>Non-payment or delayed payment</li>
                  <li>Quality of work delivered</li>
                  <li>Contract breaches between clients and professionals</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  <strong>We strongly recommend:</strong> Creating written agreements before starting any project, including scope, timeline, and payment terms.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">8. Limitation of Liability</h2>
                <p className="leading-relaxed">
                  To the maximum extent permitted by law:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>Shoot Base is provided "as is" without warranties of any kind</li>
                  <li>We are not liable for any indirect, incidental, or consequential damages</li>
                  <li>Our total liability shall not exceed the amount you paid us (if any) in the past 12 months</li>
                  <li>We are not responsible for user conduct, content accuracy, or transaction outcomes</li>
                  <li>We do not guarantee uninterrupted or error-free service</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  You use our platform at your own risk and agree to indemnify Shoot Base from any claims arising from your use.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">9. Dispute Resolution</h2>
                
                <h3 className="text-xl font-semibold text-foreground mt-4 mb-2">9.1 Between Users</h3>
                <p className="leading-relaxed">
                  Disputes between users (clients and professionals) must be resolved directly between the parties. Shoot Base may offer mediation assistance but is not obligated to intervene.
                </p>

                <h3 className="text-xl font-semibold text-foreground mt-4 mb-2">9.2 With Shoot Base</h3>
                <p className="leading-relaxed">
                  Any disputes with Shoot Base shall be governed by the laws of India. Both parties agree to attempt informal resolution before pursuing formal legal action. If resolution fails, disputes shall be subject to the exclusive jurisdiction of courts in Mumbai, Maharashtra.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">10. Termination Policy</h2>
                <p className="leading-relaxed">
                  We may terminate or suspend your account immediately, without prior notice, for:
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-2">
                  <li>Violation of these Terms</li>
                  <li>Fraudulent or illegal activity</li>
                  <li>Repeated complaints from other users</li>
                  <li>Posting prohibited or harmful content</li>
                  <li>Abuse of platform features</li>
                </ul>
                <p className="leading-relaxed mt-3">
                  Upon termination, your right to use the platform ceases immediately. Provisions regarding liability, indemnification, and dispute resolution survive termination.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">11. Privacy</h2>
                <p className="leading-relaxed">
                  Your use of Shoot Base is also governed by our <button onClick={() => navigate('/privacy-policy')} className="text-accent hover:underline">Privacy Policy</button>, which is incorporated into these Terms by reference. Please review our Privacy Policy to understand how we collect, use, and protect your information.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">12. Changes to Terms</h2>
                <p className="leading-relaxed">
                  We reserve the right to modify these Terms at any time. We will notify users of significant changes via email or platform notification. Your continued use after changes constitutes acceptance of the new Terms. If you do not agree to the modified Terms, you must stop using our services.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">13. Governing Law</h2>
                <p className="leading-relaxed">
                  These Terms are governed by and construed in accordance with the laws of India, without regard to conflict of law principles. The United Nations Convention on Contracts for the International Sale of Goods does not apply.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">14. Severability</h2>
                <p className="leading-relaxed">
                  If any provision of these Terms is found to be invalid or unenforceable, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-4">15. Contact Information</h2>
                <p className="leading-relaxed">
                  If you have questions about these Terms, please contact us:
                </p>
                <div className="mt-4 p-4 bg-accent/5 rounded-lg border border-accent/20">
                  <p className="font-medium text-foreground">Shoot Base</p>
                  <p>Email: <a href="mailto:legal@shootbase.in" className="text-accent hover:underline">legal@shootbase.in</a></p>
                  <p>Address: Film City, Mumbai, Maharashtra, India</p>
                  <p>Phone: +91 12345 67890</p>
                </div>
              </div>

              <div className="mt-12 p-6 bg-accent/5 rounded-lg border-2 border-accent/20">
                <p className="text-sm leading-relaxed text-foreground">
                  <strong>By using Shoot Base, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.</strong>
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfServicePage;
